import { createContext, useContext, ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { auth, User } from '@/lib/api'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => void
  loginSteam: () => void
  loginEpic: () => void
  loginXbox: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  
  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const result = await auth.getSession()
      return result.data?.user ?? null
    },
    retry: false,
  })
  
  const logoutMutation = useMutation({
    mutationFn: auth.logout,
    onSuccess: () => {
      queryClient.setQueryData(['session'], null)
    },
  })
  
  const value: AuthContextType = {
    user: data ?? null,
    isLoading,
    isAuthenticated: !!data,
    logout: () => logoutMutation.mutate(),
    loginSteam: auth.loginSteam,
    loginEpic: auth.loginEpic,
    loginXbox: auth.loginXbox,
  }
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
